import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { UserPlus, Link2, Heart, Building2, Settings, BarChart3 } from 'lucide-react'
import ParishApplicationModal from '../../components/ParishApplicationModal'
import { usePageMeta } from '@/hooks/usePageMeta'

export default function HowItWorks() {
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  usePageMeta({
    title: 'How It Works | Claritas',
    description:
      'Learn how Claritas connects Catholic donors and parishes. Create an account, link your bank, and start giving in minutes with secure round-up and one-time donations.',
  })

  return (
    <div className="min-h-screen bg-background-light">

      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            The Platform
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            How It Works
          </h1>
          <p className="text-base text-charcoal-muted max-w-2xl mx-auto">
            Claritas connects Catholic donors and parishes through a simple, secure giving platform.
            Whether you want to support your parish or manage your community's giving, getting started
            takes just minutes.
          </p>
        </div>
      </section>

      {/* For Donors */}
      <section className="bg-background-light py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              For Donors
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Start Giving in Three Steps
            </h2>
            <p className="text-base text-charcoal-muted max-w-xl mx-auto">
              Set up automated giving and support your parish with every purchase you make.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-surface-border rounded shadow-card text-center hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                1
              </div>
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Create Your Account</h3>
              <p className="text-sm text-charcoal-muted">
                Sign up with your email and select your parish from our directory of registered Catholic communities.
              </p>
            </div>

            <div className="bg-white p-8 border border-surface-border rounded shadow-card text-center hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                2
              </div>
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mx-auto mb-4">
                <Link2 className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Link Your Bank</h3>
              <p className="text-sm text-charcoal-muted">
                Securely connect your bank account through Stripe, the payments platform trusted by
                millions of businesses.
              </p>
            </div>

            <div className="bg-white p-8 border border-surface-border rounded shadow-card text-center hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                3
              </div>
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mx-auto mb-4">
                <Heart className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Start Giving</h3>
              <p className="text-sm text-charcoal-muted">
                Enable round-ups to donate spare change from every purchase, or make a direct one-time
                contribution to your parish anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Parishes */}
      <section className="bg-white py-20 border-t border-surface-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              For Parishes
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Onboard Your Community
            </h2>
            <p className="text-base text-charcoal-muted max-w-xl mx-auto mb-8">
              Register your parish and start receiving secure, transparent donations from your community members.
            </p>
            <Button onClick={() => setModalOpen(true)} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
              Apply for Parish Access
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-background-light p-8 border border-surface-border rounded text-center hover:border-gold/30 transition-all duration-150">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                1
              </div>
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Register Your Parish</h3>
              <p className="text-sm text-charcoal-muted">
                Create an administrator account for your parish. Provide basic details so donors can find
                and connect with your community.
              </p>
            </div>

            <div className="bg-background-light p-8 border border-surface-border rounded text-center hover:border-gold/30 transition-all duration-150">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                2
              </div>
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mx-auto mb-4">
                <Settings className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Configure Your Account</h3>
              <p className="text-sm text-charcoal-muted">
                Set up your payment details through our secure Stripe integration to receive donations
                directly to your parish's account.
              </p>
            </div>

            <div className="bg-background-light p-8 border border-surface-border rounded text-center hover:border-gold/30 transition-all duration-150">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                3
              </div>
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Track and Manage</h3>
              <p className="text-sm text-charcoal-muted">
                Monitor donations in real-time through your admin dashboard. View transaction history,
                donor activity, and giving trends.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-light py-20 border-t border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Get Started
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
            Ready to Begin?
          </h2>
          <p className="text-base text-charcoal-muted mb-8 max-w-xl mx-auto">
            Join the Claritas community and make every transaction an act of generosity toward your faith community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" onClick={() => navigate('/sign-in')} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
              Get Started
            </Button>
            <Button variant="ghost" size="lg" onClick={() => navigate('/for-donors')}>
              Learn More
            </Button>
          </div>
        </div>
      </section>
      <ParishApplicationModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}
